import type { Appointment } from '@dnx/contracts';
import { Alert, Button, cn, Dialog, Field, Input, Select, Spinner } from '@dnx/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarCheck, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api.js';
import { addDays, hhmm, longDate, professionalsQuery, slotsQuery, todayLocal } from './queries.js';

/**
 * Agendar (ou remarcar) em poucos cliques: dia → horário livre (por profissional) → confirmar.
 * Só mostra horários que o banco considera livres; se alguém reservar antes, o erro explica.
 */
export function BookDialog({ open, onClose, contactId, contactName, reschedule, onDone }: {
  open: boolean; onClose: () => void; contactId: string; contactName: string;
  reschedule?: Appointment; onDone?: (a: Appointment) => void;
}) {
  const qc = useQueryClient();
  const [date, setDate] = useState(() => addDays(todayLocal(), 1));
  const [pro, setPro] = useState<string>(reschedule?.professional?.id ?? '');
  const [picked, setPicked] = useState<{ professionalId: string; startsAt: string } | null>(null);
  const [notify, setNotify] = useState(true);
  const { data: pros } = useQuery({ ...professionalsQuery, enabled: open });
  const { data: slots, isFetching } = useQuery({ ...slotsQuery(date, pro || undefined), enabled: open });

  const save = useMutation({
    mutationFn: () => reschedule
      ? api<Appointment>(`/scheduling/appointments/${reschedule.id}/reschedule`, { method: 'POST', body: { startsAt: picked!.startsAt, version: reschedule.version, notifyPatient: notify } })
      : api<Appointment>('/scheduling/appointments', { method: 'POST', body: { contactId, professionalId: picked!.professionalId, startsAt: picked!.startsAt, notifyPatient: notify } }),
    onSuccess: (a) => {
      void qc.invalidateQueries({ queryKey: ['scheduling'] });
      void qc.invalidateQueries({ queryKey: ['crm'] });
      void qc.invalidateQueries({ queryKey: ['inbox'] });
      setPicked(null);
      onDone?.(a);
      onClose();
    },
    onError: () => { setPicked(null); void qc.invalidateQueries({ queryKey: ['scheduling', 'slots'] }); },
  });

  const noPros = pros && pros.length === 0;
  return (
    <Dialog open={open} onClose={onClose} title={reschedule ? 'Remarcar' : 'Agendar avaliação'} description={contactName}>
      <div className="grid gap-4 p-5">
        {noPros ? (
          <Alert tone="warning" title="Nenhum profissional com agenda">
            Cadastre o horário de atendimento de um médico (comando <code>professional:set</code>).
          </Alert>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-[1fr_1fr]">
              <Field label="Dia">
                <div className="flex gap-1">
                  <Button variant="secondary" size="icon" aria-label="Dia anterior" onClick={() => { setPicked(null); setDate(addDays(date, -1)); }}><ChevronLeft className="size-4" /></Button>
                  <Input type="date" value={date} min={todayLocal()} onChange={(e) => { setPicked(null); if (e.target.value) setDate(e.target.value); }} />
                  <Button variant="secondary" size="icon" aria-label="Próximo dia" onClick={() => { setPicked(null); setDate(addDays(date, 1)); }}><ChevronRight className="size-4" /></Button>
                </div>
              </Field>
              <Field label="Profissional">
                <Select value={pro} disabled={!!reschedule} onChange={(e) => { setPicked(null); setPro(e.target.value); }}>
                  <option value="">Qualquer um</option>
                  {pros?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </Select>
              </Field>
            </div>
            <p className="text-[13px] font-medium text-fg first-letter:uppercase">{longDate(date)}</p>
            <div className="min-h-24 space-y-4">
              {isFetching && !slots && <Spinner className="size-5 text-fg-subtle" label="Buscando horários" />}
              {slots?.professionals.map((p) => {
                const name = pros?.find((x) => x.id === p.professionalId)?.name ?? '';
                return (
                  <div key={p.professionalId}>
                    {!pro && <p className="mb-2 text-[12.5px] font-medium text-fg-muted">{name}</p>}
                    {p.slots.length === 0 ? <p className="text-[13px] text-fg-subtle">Sem horários livres neste dia.</p> : (
                      <div className="flex flex-wrap gap-1.5" role="listbox" aria-label={`Horários de ${name}`}>
                        {p.slots.map((s) => {
                          const sel = picked?.startsAt === s && picked.professionalId === p.professionalId;
                          return (
                            <button key={s} type="button" role="option" aria-selected={sel}
                              onClick={() => setPicked({ professionalId: p.professionalId, startsAt: s })}
                              className={cn('h-9 min-w-16 rounded-md border px-2.5 text-[13px] font-medium tabular-nums transition-colors',
                                sel ? 'border-primary bg-primary text-primary-fg' : 'border-border-strong bg-surface text-fg hover:border-primary')}>
                              {hhmm(s)}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <label className="flex items-center gap-2.5 text-[13px] text-fg">
              <input type="checkbox" className="size-4 accent-[var(--dnx-primary)]" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
              Avisar o paciente no WhatsApp
            </label>
            {save.error && <Alert tone="warning">{save.error.message}</Alert>}
            <div className="flex justify-end gap-2 border-t border-border pt-4">
              <Button variant="ghost" onClick={onClose}>Cancelar</Button>
              <Button disabled={!picked} loading={save.isPending} onClick={() => save.mutate()}>
                <CalendarCheck className="size-4" />{picked ? `${reschedule ? 'Remarcar' : 'Agendar'} às ${hhmm(picked.startsAt)}` : 'Escolha um horário'}
              </Button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}
